import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { createLogger } from '../../../shared/config/logger';
import { extractErrorMessage } from '../../../shared/utils/errorHandling';
import { redactSensitive } from '../../../shared/utils/logSanitization';
import { validateEmail, validatePassword } from '../../../shared/utils/validators';
const logger = createLogger('Login.jsx');

/**
 * Login Component
 *
 * This component handles user authentication for the LoyalPass platform.
 * It provides a form for users to enter their credentials and sign in to their account.
 * It also includes functionality for password reset.
 *
 * The login flow:
 * 1. User enters email and password
 * 2. On submit, the component attempts to authenticate with Supabase
 * 3. Upon successful login, the user is redirected to dashboard
 * 4. If login fails, an appropriate error message is displayed
 */
export default function Login() {
    // State variables to manage form inputs and component state
    const [formData, setFormData] = useState({ email: '', password: '' });
    const [state, setState] = useState({ error: null, loading: false, message: null });
    const [showPassword, setShowPassword] = useState(false);
    const { login } = useAuth();

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    // Hook for programmatic navigation
    const navigate = useNavigate();

    const validateForm = (formData) => {
        const { email, password } = formData;

        const emailValidation = validateEmail(email);
        if (!emailValidation.valid) {
            return emailValidation.error;
        }

        const passwordValidation = validatePassword(password);
        if (!passwordValidation.valid) {
            return passwordValidation.error;
        }

        // No errors found
        return null;
    };

    /**
     * Form submission handler
     *
     * This function processes the login form submission:
     * - Prevents default form submission behavior
     * - Sets loading state and clears previous errors
     * - Validates the form inputs
     * - Attempts to authenticate the user with Supabase
     * - Handles success and error cases
     *
     * @param {Event} e - The form submission event
     */
    const handleSubmit = async (e) => {
        e.preventDefault();
        setState((prevState) => ({
            ...prevState,
            error: null,
            loading: true,
            message: 'Form submitted. Please wait...',
        }));

        const errorMessage = validateForm(formData);
        if (errorMessage) {
            setState((prevState) => ({ ...prevState, error: errorMessage, loading: false }));
            return;
        }

        setState((prevState) => ({ ...prevState, error: null, message: 'Login initiated...' }));

        logger.info('Login form submitted', redactSensitive(formData));
        try {
            await login(formData);
            logger.info('User redirected to dashboard after successful login');
            setState({
                error: null,
                loading: true,
                message: 'Login successful! Redirecting you to your dashboard...',
            });

            // Give the user time to see the success message before redirecting
            setTimeout(() => {
                setState({
                    error: null,
                    loading: false,
                    message: 'Login successful! Redirecting you to your dashboard...',
                });
                navigate('/dashboard');
            }, 2000);
        } catch (error) {
            const errorMessage = extractErrorMessage(error);
            logger.error('Error in login form submission', { error: errorMessage });
            setState((prevState) => ({
                ...prevState,
                error: errorMessage,
                loading: false,
                message: null,
            }));
        }
    };

    /**
     * Password reset handler
     *
     * This function initiates the password reset process:
     * - Validates that an email has been entered
     * - Sets loading state and clears previous errors
     * - Requests a password reset email from Supabase
     * - Handles success and error cases
     */
    const handleResetPassword = async () => {
        logger.info('Password reset button clicked');
        setState((prevState) => ({
            ...prevState,
            error: null,
            message: 'Redirecting you to password reset page',
            loading: false,
        }));
        navigate('/reset-password');
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4">
            <form
                onSubmit={handleSubmit}
                className="w-full max-w-md bg-white p-8 rounded-2xl shadow-lg space-y-6"
            >
                <h2 className="text-2xl font-semibold text-center text-gray-800">
                    Sign in to{' '}
                    <span className="text-xl font-bold text-cta">
                        Loyal<span className="text-secondary">Pass</span>
                    </span>
                </h2>

                {state.error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                        {state.error}
                    </div>
                )}
                {state.message && !state.error && (
                    <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded">
                        {state.message}
                    </div>
                )}

                <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Email"
                    disabled={state.loading}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3498db] transition"
                    required
                />

                {/* Password input field with toggle */}
                <div className="relative">
                    <input
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        placeholder="Password"
                        disabled={state.loading}
                        className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3498db] transition"
                        required
                    />
                    <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                        disabled={state.loading}
                    >
                        {showPassword ? (
                            <svg
                                className="h-5 w-5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                                />
                            </svg>
                        ) : (
                            <svg
                                className="h-5 w-5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                />
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                />
                            </svg>
                        )}
                    </button>
                </div>

                <div className="flex justify-end">
                    <button
                        type="button"
                        onClick={handleResetPassword}
                        className="text-sm text-primary hover:underline"
                        disabled={state.loading}
                    >
                        Forgot Password?
                    </button>
                </div>

                <button
                    type="submit"
                    className="w-full bg-card text-white py-3 rounded-lg hover:bg-primary transition font-medium"
                    disabled={state.loading}
                >
                    {state.loading ? 'Signing in...' : 'Login'}
                </button>

                <button
                    type="button"
                    onClick={() => navigate('/register')}
                    className="w-full bg-gray-200 text-gray-800 py-3 rounded-lg hover:bg-gray-300 transition"
                    disabled={state.loading}
                >
                    Sign up instead
                </button>
            </form>
        </div>
    );
}
